import { Link } from "@/lib/router-compat";
import { ArrowLeft } from "lucide-react";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-foreground mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-10">Last updated: February 19, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">1. Information We Collect</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We collect the following types of information to provide and improve our service:
            </p>
            <ul className="text-muted-foreground leading-relaxed space-y-2 list-disc pl-5">
              <li><strong className="text-foreground">Account Information:</strong> Name, email address, and organization details provided during registration.</li>
              <li><strong className="text-foreground">Usage Data:</strong> Information about how you interact with the platform, including pages visited, features used, and actions taken.</li>
              <li><strong className="text-foreground">Uploaded Content:</strong> Documents, images, and data you upload to the platform for proposal generation and analysis.</li>
              <li><strong className="text-foreground">Payment Information:</strong> Payment processing is handled entirely by Stripe. We do not store your credit card numbers or banking details on our servers. We may retain Stripe customer IDs and subscription status for account management.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">2. How We Use Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We use the information we collect for the following purposes:
            </p>
            <ul className="text-muted-foreground leading-relaxed space-y-2 list-disc pl-5">
              <li>To operate, maintain, and improve the Sentinel Forge Systems platform and its features.</li>
              <li>To process payments and manage your subscription through Stripe.</li>
              <li>To provide customer support and respond to your inquiries.</li>
              <li>To send service-related notifications, such as billing updates and security alerts.</li>
              <li>To analyze usage trends and optimize the user experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">3. Data Sharing</h2>
            <ul className="text-muted-foreground leading-relaxed space-y-2 list-disc pl-5">
              <li><strong className="text-foreground">Stripe:</strong> We share necessary information with Stripe to process payments and manage subscriptions.</li>
              <li><strong className="text-foreground">No Selling of Data:</strong> We do not sell, rent, or trade your personal information to any third parties.</li>
              <li><strong className="text-foreground">Essential Service Providers:</strong> We may share data with trusted third-party providers who assist in operating our service (e.g., email delivery, hosting), subject to confidentiality agreements.</li>
              <li><strong className="text-foreground">Legal Requirements:</strong> We may disclose information if required to do so by law or in response to valid legal process.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">4. Cookies & Tracking</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use cookies and similar technologies to maintain your session, remember your preferences, and ensure the platform functions correctly. These are essential cookies required for the operation of the service. We do not use cookies for advertising or cross-site tracking purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">5. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement reasonable technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include encryption of data in transit, secure authentication mechanisms, and regular security reviews. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">6. User Rights</h2>
            <p className="text-muted-foreground leading-relaxed">
              You have the right to request access to, correction of, or deletion of your personal data at any time. To exercise these rights, please contact us at the email address provided below. We will respond to your request within a reasonable timeframe and in accordance with applicable data protection laws. Please note that deletion of certain data may impact your ability to use the service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">7. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Sentinel Forge Systems is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal data from a child under 13, we will take steps to delete such information promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">8. Changes to Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. Changes will be posted on this page with an updated "Last updated" date. Your continued use of the service after any changes constitutes your acceptance of the revised policy. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">9. Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at{" "}
              <a href="mailto:privacy@sentinelforgesystems.com" className="text-primary hover:underline">privacy@sentinelforgesystems.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
